import { useCallback, useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthData from './useAuthData';
import { 
  AddBusinessOutlined, 
  AddchartOutlined, 
  BookmarksOutlined, 
  CasesOutlined, 
  CrisisAlertOutlined, 
  EnhancedEncryptionOutlined, 
  FactoryOutlined, 
  Home, 
  LockPerson, 
  PersonAddOutlined, 
  QuestionAnswerOutlined, 
  SettingsRounded, 
  StorefrontOutlined,
  VerifiedUserRounded,
} from '@mui/icons-material';
import useApi from './useApi';
import { jwtDecode } from 'jwt-decode';

const CATEGORY_CONFIG = {
  GESTÃO: {
    icon: <CrisisAlertOutlined style={{ fontSize: '20px' }}/>,
    show: false
  },
  ADMINISTRACAO: {
    icon: <CasesOutlined style={{ fontSize: '20px' }}/>,
    show: false
  },
  COMERCIAL: {
    icon: <StorefrontOutlined style={{ fontSize: '20px' }}/>,
    show: false
  },
  INDUSTRIAL: {
    icon: <FactoryOutlined style={{ fontSize: '20px' }}/>,
    show: false
  },
  MARKETING: {
    icon: <QuestionAnswerOutlined style={{ fontSize: '20px' }}/>,
    show: false
  }
};

const findMatchingMenuItemByUrl = (items, currentPath, foundPath = []) => {
  for (const item of items) {
    const currentFoundPath = [...foundPath, item];
    
    // Verifica match direto da URL
    if (item.path === currentPath) {
      return currentFoundPath;
    }
    
    // Verifica match para URLs de relatório
    if (currentPath.startsWith('/relatorio/') && item.path) {
      const urlLinkId = currentPath.split('/').pop();
      if (item.link_id === urlLinkId || item.path === `/relatorio/${urlLinkId}`) {
        return currentFoundPath;
      }
    }
    
    // Procura recursivamente nos filhos
    if (item.children) {
      const foundInChildren = findMatchingMenuItemByUrl(item.children, currentPath, currentFoundPath);
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }
  return null;
};

const useMenuControl = () => {
  const location = useLocation();
  const { userData, userPermissions, hasPermission } = useAuthData();
  const [dynamicMenuItems, setDynamicMenuItems] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [openMenuKeys, setOpenMenuKeys] = useState([]);

  const token = localStorage.getItem('token');
  const decodedToken = jwtDecode(token);
  const userId = decodedToken.usuario_id;
  const tipo_usuario = decodedToken.tipo_usuario;
  
  const urlApi = tipo_usuario === 'administrador'
    ? 'https://vision.alfama.com/api/view/usuarios/administrador/dashboard'
    : `https://vision.alfama.com/api/view/usuarios/dashboard/${userId}`;
  
  const { callApi } = useApi(urlApi, { includeToken: true });

  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        const response = await callApi();
        if (response) {
          // Reseta a configuração de exibição das categorias
          Object.keys(CATEGORY_CONFIG).forEach(key => {
            CATEGORY_CONFIG[key].show = false;
          });

          // Agrupa os dashboards por categoria e departamento
          const groupedDashboards = response.reduce((acc, item) => {
            const category = item.nome_categoria_departamento || 'Outros';
            const dept = item.nome_departamento;
            
            if (!acc[category]) acc[category] = {};
            if (!acc[category][dept]) acc[category][dept] = [];
            
            // Adiciona informações completas do dashboard
            acc[category][dept].push({
              key: `dashboard-${item.dashboard_id}`,
              label: item.nome_dashboard,
              path: `/relatorio/${item.link_id}`,
              dashboard_id: item.dashboard_id,
              link_id: item.link_id
            });
            
            if (CATEGORY_CONFIG[category.toUpperCase()]) {
              CATEGORY_CONFIG[category.toUpperCase()].show = true;
            }
            
            return acc;
          }, {});

          // Cria o menu dinâmico
          const dynamicMenu = Object.entries(groupedDashboards).map(([category, departments], catIndex) => {
            const categoryConfig = CATEGORY_CONFIG[category.toUpperCase()];
            
            return {
              key: `cat-${catIndex + 3}`,
              submenu: true,
              icon: categoryConfig?.icon || <CasesOutlined style={{ fontSize: '20px' }}/>,
              label: category,
              children: Object.entries(departments).map(([dept, dashboards], deptIndex) => ({
                key: `cat-${catIndex + 3}-${deptIndex}`,
                label: dept,
                children: dashboards
              }))
            };
          }).filter(menu => {
            const categoryConfig = CATEGORY_CONFIG[menu.label.toUpperCase()];
            return !categoryConfig || categoryConfig.show;
          });

          setDynamicMenuItems(dynamicMenu);
        }
      } catch (error) {
        console.error('Erro ao buscar dashboards:', error);
      }
    };

    if (userId) {
      fetchDashboards();
    }
  }, [userId, callApi]);

  const MENU_CONFIG = useMemo(() => {
    // Criar um objeto para armazenar os itens de menu fixos
    const menuItems = {
      INICIO: {
        key: '1',
        icon: <Home style={{ fontSize: '20px' }}/>,
        label: 'Página Inicial',
        path: '/inicio',
        permission: 'usuario'
      },
      FAVORITOS: {
        key: '99',
        icon: <BookmarksOutlined style={{ fontSize: '20px' }}/>,
        label: 'Favoritos',
        path: '/favoritos',
        permission: 'usuario'
      },
      CONFIGURACOES: {
        key: '2',
        submenu: true,
        icon: <SettingsRounded style={{ fontSize: '20px' }}/>,
        label: 'Configurações',
        permission: 'gerenciador',
        children: [
          {
            key: '2-2',
            icon: <EnhancedEncryptionOutlined style={{ fontSize: '20px' }}/>,
            label: 'Grupos de Controle',
            path: '/grupos',
            permission: 'gerenciador'
          },
          {
            key: '2-3',
            icon: <AddBusinessOutlined style={{ fontSize: '20px' }} />,
            label: 'Departamentos',
            path: '/departamentos',
            permission: 'gerenciador'
          },
          {
            key: '2-4',
            icon: <AddchartOutlined style={{ fontSize: '20px' }}/>,
            label: 'Dashboards',
            path: '/dashboards',
            permission: 'gerenciador'
          },
          {
            key: '2-1',
            icon: <PersonAddOutlined style={{ fontSize: '20px' }}/>,
            label: 'Usuários',
            path: '/usuarios',
            permission: 'gerenciador'
          },
          {
            key: '2-5',
            icon: <LockPerson style={{ fontSize: '20px' }}/>,
            label: 'Acessos',
            path: '/usuarios/logs',
            permission: 'administrador'
          },
          {
            key: '2-6',
            icon: <VerifiedUserRounded style={{ fontSize: '20px' }}/>,
            label: 'Permissões',
            path: '/usuarios/permissoes',
            permission: 'administrador'
          },
        ]
      }
    };

    // Separar o item GERENCIA dos outros itens dinâmicos
    let gerenciaItem = null;
    const otherItems = {};

    // Procurar por GERENCIA nas categorias dinâmicas
    dynamicMenuItems.forEach(item => {
      if (item.label.toUpperCase() === 'GESTÃO') {
        gerenciaItem = item;
      } else {
        otherItems[item.label.toUpperCase()] = item;
      }
    });

    // Montar o MENU_CONFIG final na ordem desejada
    const finalMenuConfig = { ...menuItems };
    
    // Adicionar GERENCIA logo após CONFIGURACOES (se existir)
    if (gerenciaItem) {
      finalMenuConfig['GESTÃO'] = gerenciaItem;
    }
    
    // Adicionar os demais itens dinâmicos
    return { ...finalMenuConfig, ...otherItems };
  }, [dynamicMenuItems]);

  const processMenuChildren = useCallback((children) => {
    if (!children) return [];
    
    return children
      .filter(child => !child.permission || hasPermission(child.permission))
      .map(child => ({
        key: child.key,
        icon: child.icon,
        label: child.label,
        path: child.path,
        dashboard_id: child.dashboard_id,
        link_id: child.link_id,
        ...(child.children && {
          submenu: child.submenu,
          children: processMenuChildren(child.children)
        })
      }))
      .filter(child => !child.children || child.children.length > 0);
  }, [hasPermission]);

  const generateMenuItems = useCallback(() => {
    const menuItems = [];

    Object.values(MENU_CONFIG).forEach(menuItem => {
      if (menuItem.permission && !hasPermission(menuItem.permission)) return;

      if (menuItem.submenu) {
        const children = menuItem.permission ? 
          processMenuChildren(menuItem.children) : 
          processMenuChildren(menuItem.children || []);
        
        if (children && children.length > 0) {
          menuItems.push({
            key: menuItem.key,
            icon: menuItem.icon,
            label: menuItem.label,
            submenu: menuItem.submenu,
            children
          });
        }
      } else {
        menuItems.push({
          key: menuItem.key,
          icon: menuItem.icon,
          label: menuItem.label,
          path: menuItem.path
        });
      }
    });

    return menuItems;
  }, [MENU_CONFIG, hasPermission, processMenuChildren]);

  useEffect(() => {
    const currentPath = location.pathname;
    const allMenuItems = generateMenuItems();
    
    const matchingItems = findMatchingMenuItemByUrl(allMenuItems, currentPath);
    
    if (matchingItems) {
      const keys = matchingItems.map(item => item.key);
      const selectedKey = keys[keys.length - 1];
      const parentKeys = keys.slice(0, -1);
      
      setActiveKeys([selectedKey]);
      setOpenMenuKeys(prev => {
        const newKeys = Array.from(new Set([...prev, ...parentKeys]));
        return newKeys;
      });
    } else {
      setActiveKeys([]);
      setOpenMenuKeys([]);
    }
  }, [location.pathname, generateMenuItems]);

  return {
    menuItems: generateMenuItems(),
    selectedKeys: activeKeys,
    openKeys: openMenuKeys,
    MENU_CONFIG,
    userData,
    userPermissions
  };
};

export default useMenuControl;